import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Mail, LifeBuoy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * "Still need help?" strip — reads support_email + support_form_url from
 * the public `/email/public/support-contacts` endpoint (no auth needed).
 * Renders nothing if BOTH values are unset, so a fresh install with no
 * config doesn't show a dead strip. Individual halves gracefully hide
 * if only one of the two values is configured.
 *
 * `variant='footer'` — big block at the bottom of a hub/list page.
 * `variant='article'` — compact end-of-article Yes/No prompt.
 */
const SupportCTA = ({ variant = 'footer' }) => {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/email/public/support-contacts`)
      .then((r) => { if (alive) setCfg(r.data || {}); })
      .catch(() => { if (alive) setCfg({}); });
    return () => { alive = false; };
  }, []);

  if (!cfg) return null;
  const email = (cfg.support_email || '').trim();
  const formUrl = (cfg.support_form_url || '').trim();
  if (!email && !formUrl) return null;

  if (variant === 'article') {
    return (
      <div className="mt-10 border-t pt-6" data-testid="support-cta-article">
        <p className="text-sm text-slate-700 mb-3">
          <strong>Did this article solve your problem?</strong>
        </p>
        <div className="flex flex-wrap items-center gap-3">
          {formUrl ? (
            <a
              href={formUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#0A7EA4] hover:bg-[#08678A] text-white text-sm font-medium transition"
              data-testid="support-cta-form-link"
            >
              <LifeBuoy className="w-4 h-4" /> No — submit a support ticket
            </a>
          ) : null}
          {email ? (
            <a
              href={`mailto:${email}?subject=Support%20request`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm transition"
              data-testid="support-cta-email-link-article"
            >
              <Mail className="w-4 h-4" /> Email us
            </a>
          ) : null}
        </div>
      </div>
    );
  }

  // Footer variant (default)
  return (
    <div
      className="mt-10 rounded-xl border border-slate-200 bg-gradient-to-r from-slate-50 to-white p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
      data-testid="support-cta-footer"
    >
      <div>
        <p className="font-semibold text-slate-900">Still need help?</p>
        <p className="text-sm text-slate-600 mt-0.5">
          Can&apos;t find what you&apos;re looking for? We usually respond within 1 business day.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {formUrl ? (
          <a
            href={formUrl} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#0A7EA4] hover:bg-[#08678A] text-white text-sm font-medium transition"
            data-testid="support-cta-form-link"
          >
            <LifeBuoy className="w-4 h-4" /> Submit a support ticket
          </a>
        ) : null}
        {email ? (
          <a
            href={`mailto:${email}?subject=Support%20request`}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm transition"
            data-testid="support-cta-email-link"
          >
            <Mail className="w-4 h-4" /> Email {email}
          </a>
        ) : null}
      </div>
    </div>
  );
};

export default SupportCTA;
